/** @jsx jsx */

/** @jsxFrag */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';
import type { FetchResult } from 'react-apollo';

import { useMutation } from '@apollo/client/react';

import { Dialog } from '@coursera/cds-core';
import { email, required, runValidations } from '@coursera/coursera-ui/lib/utils/validation';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';
import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import AddRecoveryEmailStep from 'bundles/enterprise-legacy-learner-home/components/modals/AddRecoveryEmailStep';
import ConfirmRecoveryEmailStep from 'bundles/enterprise-legacy-learner-home/components/modals/ConfirmRecoveryEmailStep';
import { STEPS } from 'bundles/enterprise-legacy-learner-home/constants/forcedSSORecoveryEmail';
import useAlternativeEmailModal from 'bundles/enterprise-legacy-learner-home/queryHooks/useAlternativeEmailModal';
import DeleteUserEmailMutation from 'bundles/saml-sso/components/queries/DeleteUserEmailMutation.graphql';
import type {
  InsertUserEmailMutationResult,
  InsertUserEmailMutationVariables,
} from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import { InsertUserEmailMutation } from 'bundles/saml-sso/components/queries/InsertUserEmailMutation';
import type {
  UserEmails_DeleteUserEmailMutation as DeleteUserEmailMutationType,
  UserEmails_DeleteUserEmailMutationVariables as DeleteUserEmailMutationVariables,
} from 'bundles/saml-sso/components/queries/__generated__/DeleteUserEmailMutation';
import { getUnverifiedNonPrimaryEmail } from 'bundles/saml-sso/shared/utils/email';
import { useUserEmails } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type WrapperProps = {
  userId: number;
  programId: string;
};

type Props = {
  userId: number;
  isOpen: boolean;
  onClose: () => void;
  userEmails: UserEmail[];
  onUpdate: () => void;
  shouldDeferOnClose?: boolean;
};

const buttonNames: Record<string, ButtonName> = {
  addRecoveryEmail: 'add_recovery_email_modal_button',
  closeRecoveryEmail: 'close_recovery_email_modal_button',
};
const inputName = 'recovery_email_modal_input';
export const ForcedSSORecoveryEmailModal = ({
  isOpen,
  userId,
  userEmails,
  onUpdate,
  onClose,
  shouldDeferOnClose,
}: Props) => {
  const unverifiedNonPrimaryEmail = getUnverifiedNonPrimaryEmail(userEmails);
  const [emailField, setEmailField] = useState<string>(unverifiedNonPrimaryEmail?.emailAddress ?? '');
  const [emailFieldError, setEmailFieldError] = useState<string>('');
  const track = useTracker();
  const [currentStep, setCurrentStep] = useState<keyof typeof STEPS>(
    unverifiedNonPrimaryEmail ? STEPS.CONFIRM_ADD : STEPS.ADD_EMAIL
  );
  const thirdPartyOrgContext = useThirdPartyOrganization();
  const eventingEnterpriseProp = React.useMemo(
    () =>
      thirdPartyOrgContext?.slug && {
        enterprise: { organizationSlug: thirdPartyOrgContext.slug },
      },
    [thirdPartyOrgContext?.slug]
  );

  const addRecoveryEmailButton: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: { name: buttonNames.addRecoveryEmail },
      ...eventingEnterpriseProp,
    },
    [isOpen, currentStep]
  );

  const cancelButton: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker(
    'view_button',
    {
      button: { name: buttonNames.closeRecoveryEmail },
      ...eventingEnterpriseProp,
    },
    [isOpen, currentStep]
  );

  React.useEffect(() => {
    if (isOpen && unverifiedNonPrimaryEmail?.emailAddress) {
      setCurrentStep(STEPS.CONFIRM_ADD);
      setEmailField(unverifiedNonPrimaryEmail.emailAddress);
    }
  }, [unverifiedNonPrimaryEmail?.emailAddress, isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      const isAddEmailStep = currentStep === STEPS.ADD_EMAIL;
      track('view_modal', {
        modal: { name: isAddEmailStep ? 'add_recovery_email_modal' : 'resend_recovery_email_modal' },
        ...eventingEnterpriseProp,
      });
    }
  }, [isOpen, currentStep, track, eventingEnterpriseProp]);

  const onEmailUpdate = () => {
    setEmailFieldError('');
    setCurrentStep(STEPS.CONFIRM_ADD);
    onUpdate();
  };

  const onCloseRecoveryEmail = () => {
    onClose();
    track('click_button', {
      button: { name: buttonNames.closeRecoveryEmail },
      ...eventingEnterpriseProp,
    });
  };

  const [deleteUserEmail, { loading: isRemovingUnverifiedEmails }] = useMutation<
    DeleteUserEmailMutationType,
    DeleteUserEmailMutationVariables
  >(DeleteUserEmailMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const [insertUserEmail, { loading }] = useMutation<InsertUserEmailMutationResult, InsertUserEmailMutationVariables>(
    InsertUserEmailMutation,
    {
      onError: ({ networkError }) => {
        // @ts-expect-error(apollo-v3): TS2345: Argument of type 'readonly GraphQLError[] | undefined' is not assignable
        const statusCode = networkError?.response?.status;
        if (statusCode === 409) {
          setEmailFieldError(_t('This email is being used with another account! Try using a different email address'));
        } else {
          setEmailFieldError(_t('There was an error while adding the email, please try again'));
        }
        track('click_button', {
          button: { name: buttonNames.addRecoveryEmail, clickStatus: 'invalid' },
          ...eventingEnterpriseProp,
        });
        track('validate_text_input', {
          textInput: { name: inputName },
          status: 'failure',
          errorCode: `${statusCode ?? 'network_error'}`,
          ...eventingEnterpriseProp,
        });
      },
      onCompleted: (data) => {
        const addedEmail = data?.InsertUserEmailResponse?.userEmail?.emailAddress;
        if (addedEmail) {
          onEmailUpdate();
          track('click_button', {
            button: { name: buttonNames.addRecoveryEmail, clickStatus: 'valid' },
            ...eventingEnterpriseProp,
          });
          track('validate_text_input', {
            textInput: { name: inputName },
            status: 'success',
            ...eventingEnterpriseProp,
          });
        }
      },
    }
  );

  const onChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmailField(value);
    setEmailFieldError('');
  };

  const getValidationErrors = () => {
    const errors = runValidations(emailField, [required, email]);
    return errors;
  };

  const deleteUnverifiedEmails = () => {
    const deletePromises: Promise<FetchResult<DeleteUserEmailMutationType>>[] = [];
    userEmails.forEach((emailAddr) => {
      if (!emailAddr.isPrimary && !emailAddr.isVerified && emailAddr.emailAddress) {
        const deletePromise = deleteUserEmail({
          variables: {
            input: {
              email: emailAddr.emailAddress,
            },
          },
        });
        deletePromises.push(deletePromise);
      }
    });
    return Promise.all(deletePromises);
  };

  const onSubmitEmail = async () => {
    const errors = getValidationErrors();

    if (errors) {
      track('click_button', {
        button: { name: buttonNames.addRecoveryEmail, clickStatus: 'invalid' },
        ...eventingEnterpriseProp,
      });
      track('validate_text_input', {
        textInput: { name: inputName },
        status: 'failure',
        errorCode: 'invalid_email',
        ...eventingEnterpriseProp,
      });
      setEmailFieldError(errors);
      return;
    }
    setEmailFieldError('');
    try {
      await deleteUnverifiedEmails();
      await insertUserEmail({
        variables: {
          input: { emailAddress: emailField, isPrimary: false, userId },
        },
      });
    } catch {
      setEmailFieldError(_t('There was an error while adding the email, please try again'));
    }
  };

  const getModalContent = () => {
    if (currentStep === STEPS.ADD_EMAIL) {
      return (
        <AddRecoveryEmailStep
          emailField={emailField}
          onChangeEmail={onChangeEmail}
          getValidationErrors={getValidationErrors}
          onSubmitEmail={onSubmitEmail}
          emailFieldError={emailFieldError}
          loading={loading || isRemovingUnverifiedEmails}
          onClose={onCloseRecoveryEmail}
          addRecoveryEmailButtonRef={addRecoveryEmailButton}
          cancelButtonRef={cancelButton}
          shouldDeferOnClose={shouldDeferOnClose}
        />
      );
    }

    return (
      <ConfirmRecoveryEmailStep
        userId={userId}
        emailField={emailField}
        onUpdateEmailClick={() => setCurrentStep(STEPS.ADD_EMAIL)}
        onClose={onCloseRecoveryEmail}
      />
    );
  };

  return (
    <Dialog aria-describedby="description" aria-labelledby="title" open={isOpen} variant="standard" onClose={onClose}>
      {getModalContent()}
    </Dialog>
  );
};

export const ForcedSSORecoveryEmailModalWrapper = ({ userId, programId }: WrapperProps) => {
  const { userEmails = [], refetchUserEmails } = useUserEmails();
  const { isOpen, onClose, shouldDeferOnClose } = useAlternativeEmailModal(userId, userEmails, programId);

  const onUpdate = () => {
    refetchUserEmails?.();
  };

  return (
    <ForcedSSORecoveryEmailModal
      userId={userId}
      isOpen={isOpen}
      onClose={onClose}
      userEmails={userEmails}
      onUpdate={onUpdate}
      shouldDeferOnClose={shouldDeferOnClose}
    />
  );
};

export default ForcedSSORecoveryEmailModalWrapper;
